<script setup lang="ts">
import { ref, watch, nextTick } from 'vue';
import useLocale from '../../composables/use-locale';

type Video = {
  resourceId: string;
  title: string;
  thumbnail?: string;
};

type Props = {
  videos: Video[];
  allowRemoveAll?: boolean; // If true, all videos can be removed. If false, at least one must remain
  showEdit?: boolean;
  showRemove?: boolean;
};

const props = withDefaults(defineProps<Props>(), {
  allowRemoveAll: false,
  showEdit: false,
  showRemove: true,
});

const scrollContainer = ref<HTMLElement | null>(null);

watch(
  () => props.videos.length,
  (newLen, oldLen) => {
    if (newLen > oldLen) {
      nextTick(() => {
        if (scrollContainer.value) {
          scrollContainer.value.scrollLeft = scrollContainer.value.scrollWidth;
        }
      });
    }
  },
);

const emit = defineEmits<{
  (e: 'remove', resourceId: string): void;
  (e: 'edit', resourceId: string): void;
}>();

const handleRemove = (resourceId: string, index: number) => {
  // If allowRemoveAll is false, prevent removing the first video when it's the only one or last one
  if (!props.allowRemoveAll && index === 0 && props.videos.length === 1) {
    return;
  }
  emit('remove', resourceId);
};

const { t } = useLocale();

const handleEdit = (resourceId: string) => {
  emit('edit', resourceId);
};

const canRemove = (index: number) => {
  if (!props.showRemove) {
    return false;
  }
  if (props.allowRemoveAll) {
    return true;
  }
  // First video is always locked (never removable) when allowRemoveAll=false
  if (index === 0) {
    return false;
  }
  return true;
};
</script>

<template>
  <div
    ref="scrollContainer"
    class="video-scroll-container d-flex flex-row align-start ga-6 overflow-x-auto overflow-y-hidden py-4"
    tabindex="0"
  >
    <!-- Display all videos -->
    <div
      v-for="(video, index) in videos"
      :key="video.resourceId"
      class="position-relative flex-shrink-0 d-flex flex-column align-center"
    >
      <v-img
        v-if="video.thumbnail"
        :src="video.thumbnail"
        alt="Video thumbnail"
        width="340"
        aspect-ratio="16/9"
        cover
        class="rounded-lg elevation-4"
      />
      <v-img v-else src="../assets/buzz.png" alt="Buzzer" width="200" />

      <h2
        v-if="video.title"
        class="text-white text-center text-h6 font-weight-medium"
        :class="{ 'video-title-max': videos.length > 1 }"
      >
        {{ video.title }}
      </h2>

      <div class="button-wrapper d-flex flex-row ga-2">
        <!-- Edit button -->
        <v-btn
          v-if="showEdit"
          icon
          size="small"
          color="black"
          class="edit-video-btn"
          :aria-label="t('edit_questions')"
          @click="handleEdit(video.resourceId)"
        >
          <v-icon icon="fa:fawsb fa-pencil"></v-icon>
          <v-tooltip activator="parent" location="top" aria-hidden="true">
            {{ t('edit_questions') }}
          </v-tooltip>
        </v-btn>

        <!-- Remove button -->
        <v-btn
          v-if="canRemove(index)"
          icon
          size="small"
          color="error"
          class="remove-video-btn"
          :aria-label="t('remove_video')"
          @click="handleRemove(video.resourceId, index)"
        >
          <v-icon icon="fa:fawsb fa-times"></v-icon>
          <v-tooltip activator="parent" location="top" aria-hidden="true">
            {{ t('remove_video') }}
          </v-tooltip>
        </v-btn>
      </div>
    </div>

    <slot />
  </div>
</template>

<style scoped lang="scss">
.video-scroll-container {
  max-width: 90vw;

  // Custom scrollbar styling to match Quizter theme
  &::-webkit-scrollbar {
    height: 10px;
  }

  &::-webkit-scrollbar-track {
    background: transparent;
  }

  &::-webkit-scrollbar-thumb {
    background-color: rgba(255, 255, 255, 0.3);
    border-radius: 8px;
    border: 2px solid transparent;
    background-clip: content-box;
  }

  &::-webkit-scrollbar-thumb:hover {
    background-color: rgba(255, 255, 255, 0.5);
  }
}

.video-title-max {
  max-width: 340px;
}

.button-wrapper {
  position: absolute;
  top: 8px;
  right: 8px;
  z-index: 2;
}
</style>
